/**
 * UserAvatar — lingkaran inisial dengan pembeda tier sesi (Fase 5).
 *
 *  - guest:   abu netral (eksplorasi, belum penuh)
 *  - free:    ungu brand (default yang sudah dikenal)
 *  - premium: ungu brand + ring emas + badge ✨ (tier tertinggi)
 *
 * Satu komponen dipakai Beranda + Akun supaya tidak divergen.
 */

import { StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand } from '@/constants/theme';
import type { SessionTier } from '@/lib/premium';

interface UserAvatarProps {
  /** Huruf yang tampil (inisial nama anak / nama akun). */
  initial: string;
  tier: SessionTier;
  /** Diameter lingkaran. Default 36 (Beranda); Akun memakai 44. */
  size?: number;
}

const GUEST_BG = '#e8e6ea';

export function UserAvatar({ initial, tier, size = 36 }: UserAvatarProps) {
  const radius = size / 2;
  return (
    <View
      style={styles.wrap}
      accessibilityRole="image"
      accessibilityLabel={
        tier === 'premium' ? 'Avatar Premium' : tier === 'guest' ? 'Avatar Tamu' : 'Avatar'
      }
    >
      <View
        style={[
          styles.circle,
          {
            width: size,
            height: size,
            borderRadius: radius,
            backgroundColor: tier === 'guest' ? GUEST_BG : Brand.primary,
            borderColor: tier === 'premium' ? Brand.gold : 'transparent',
            borderWidth: tier === 'premium' ? 2 : 0,
          },
        ]}
      >
        <ThemedText
          style={[
            styles.initial,
            { fontSize: size * 0.44, color: tier === 'guest' ? '#6b6573' : '#ffffff' },
          ]}
        >
          {initial.toUpperCase()}
        </ThemedText>
      </View>
      {tier === 'premium' ? (
        <View style={styles.badge} accessibilityLabel="Lencana premium">
          <ThemedText style={styles.badgeGlyph}>✨</ThemedText>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
  },
  circle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initial: {
    fontWeight: '700',
  },
  badge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeGlyph: {
    fontSize: 10,
    lineHeight: 12,
  },
});
